import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCampaign, stopCampaign, trackCampaignView } from "../api/campaigns";
import { checkCanMessage, sendMessageRequest } from "../api/messages";
import { getSubmissionsByCampaign } from "../api/submissions";
import CampaignPeopleCard from "../components/CampaignPeopleCard";
import { AppScope } from "../components/ds/AppCover";
import CampaignSubmissionModal from "../components/CampaignSubmissionModal";
import CampaignAnalytics from "../components/campaign/CampaignAnalytics";
import CampaignHero from "../components/campaign/CampaignHero";
import ConnectedAccounts from "../components/campaign/ConnectedAccounts";
import MySubmissions from "../components/campaign/MySubmissions";
import SubmissionsReview from "../components/campaign/SubmissionsReview";
import TopUpBudgetModal from "../components/campaign/TopUpBudgetModal";
import { APP_FILLED, SELECTED_TAB, PlatformLogo, acceptedPlatforms, platformForRateKey } from "../components/campaign/shared";
import Alert from "../components/ds/Alert";
import Button from "../components/ds/Button";
import Icon from "../components/ds/Icon";
import IconButton from "../components/ds/IconButton";
import InlineMessage from "../components/ds/InlineMessage";
import Modal from "../components/ds/Modal";
import ProgressBar from "../components/ds/ProgressBar";
import ViewTabs from "../components/ds/ViewTabs";
import UserProfileLink from "../components/UserProfileLink";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import { formatCampaignMoney, getCampaignSharePath, getParticipantTypeLabel } from "../utils/campaignCardUtils";
import { getPerPlatformRateRows, getPlatformShortLabel } from "../utils/campaignRates";
import { getShareUrl, shareOrCopyUrl } from "../utils/share";

const money = (value) => `${formatCampaignMoney(value || 0)} ₾`;

function fileLabelFromUrl(url) {
  try {
    const seg = new URL(url).pathname.split("/").filter(Boolean).pop();
    return seg ? decodeURIComponent(seg) : url;
  } catch {
    return url;
  }
}

const Stat = ({ label, children }) => (
  <div className="flex min-w-0 flex-col gap-space-8 rounded-[22px] bg-bg-inset p-space-16">
    <p className="text-caps-label uppercase text-label-tertiary">{label}</p>
    {children}
  </div>
);

const ResourceLink = ({ href, icon, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex min-w-0 items-center gap-space-8 rounded-14 bg-bg-inset px-space-12 py-space-8 text-subheadline text-label-primary hover:underline"
  >
    <Icon name={icon} size={16} className="text-label-secondary" />
    <span className="min-w-0 truncate">{children}</span>
  </a>
);

/** Figma V2 (visitor) and O1 (owner): the banner, then a view for each thing to do here. */
const CampaignDetailsPage = ({ isDark, setToast, onNavigate, campaignId: campaignIdProp }) => {
  const { id: idFromParams } = useParams();
  const id = campaignIdProp || idFromParams;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [submissions, setSubmissions] = useState([]);
  const [viewTracked, setViewTracked] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [showTopUp, setShowTopUp] = useState(false);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [messageStatus, setMessageStatus] = useState(null);
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [showMessageRequestModal, setShowMessageRequestModal] = useState(false);
  const [messageRequestDraft, setMessageRequestDraft] = useState("");

  useEffect(() => {
    const fetchCampaign = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      setCampaign(null);
      setViewTracked(false);
      setTab("overview");

      try {
        setLoading(true);
        const response = await getCampaign(id);
        if (response.success) {
          setCampaign(response.data);
        } else {
          setToast?.(t("campaigns.campaignNotFound"));
        }
      } catch (error) {
        console.error("Error fetching campaign:", error);
        setToast?.(t("campaigns.failedToLoadCampaign"));
      } finally {
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [id, setToast, t]);

  useEffect(() => {
    const trackView = async () => {
      if (user && campaign && !viewTracked) {
        try {
          await trackCampaignView(id);
          setViewTracked(true);
        } catch (error) {
          console.error("Error tracking view:", error);
        }
      }
    };

    trackView();
  }, [user, campaign, id, viewTracked]);

  const creatorId = campaign?.createdBy?._id;
  const userId = user?.id;

  useEffect(() => {
    if (!creatorId || !userId || creatorId === userId) return;
    checkCanMessage(creatorId)
      .then((status) => status.success && setMessageStatus(status.data))
      .catch((error) => console.error("Error fetching creator info:", error));
  }, [creatorId, userId]);

  const loadSubmissions = useCallback(async () => {
    if (!id || !userId) return;
    try {
      const response = await getSubmissionsByCampaign(id);
      if (response.success) setSubmissions(response.data || []);
    } catch (error) {
      console.error("Error fetching submissions:", error);
    }
  }, [id, userId]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const handleSendMessageRequest = async () => {
    if (!creatorId || !user) return;
    const body = messageRequestDraft.trim();
    if (!body) {
      setToast?.(t("messages.enterMessage"));
      return;
    }

    try {
      setIsSendingRequest(true);
      const response = await sendMessageRequest(creatorId, body);
      if (response.success) {
        setMessageStatus({ ...messageStatus, hasPendingRequest: true, isSender: true });
        setShowMessageRequestModal(false);
        setMessageRequestDraft("");
        window.dispatchEvent(new CustomEvent("messages-updated"));
        setToast?.(t("campaigns.messageRequestSent"));
      }
    } catch (error) {
      console.error("Error sending message request:", error);
      setToast?.(error.response?.data?.message || t("campaigns.failedToSendRequest"));
    } finally {
      setIsSendingRequest(false);
    }
  };

  const handleStop = async () => {
    setIsStopping(true);
    try {
      const mintSpaceId = campaign?.mintSpace?._id || campaign?.mintSpace || null;
      const response = await stopCampaign(id, mintSpaceId);
      if (response.success) {
        setCampaign((prev) => ({ ...prev, status: "cancelled" }));
        setShowStopModal(false);
        setToast?.(t("campaigns.campaignStopped"));
      }
    } catch (error) {
      console.error("Error stopping campaign:", error);
      setToast?.(t("errors.failedToDelete"));
    } finally {
      setIsStopping(false);
    }
  };

  const handleShare = () => shareOrCopyUrl(getShareUrl(getCampaignSharePath(campaign)), setToast, t);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] w-full items-center justify-center">
        <div className="size-16 animate-spin rounded-full border-4 border-accent-default border-t-transparent" />
      </div>
    );
  }

  if (!campaign) {
    return null;
  }

  const isOwner = Boolean(user && creatorId === user.id);
  const open = campaign.status === "active" || campaign.status === "pending";
  const platforms = acceptedPlatforms(campaign);
  const budget = campaign.budget || 0;
  const spent = campaign.spent || 0;
  const used = budget > 0 ? Math.min(spent / budget, 1) : 0;
  const rateRows = getPerPlatformRateRows(campaign);
  const views = (campaign.viewsPerRate || 1000).toLocaleString();
  const endDate = campaign.endDate ? new Date(campaign.endDate).toLocaleDateString() : t("campaigns.noEndDate");
  const requirements = Array.isArray(campaign.requirements) ? campaign.requirements : [];
  const ownSubmissions = submissions.filter((row) => String(row.submittedBy?._id || row.submittedBy) === String(userId));

  const attachments = [
    ...new Set(
      [...(campaign.additionalAssetFileUrls || []), campaign.additionalAssetsFile]
        .map((url) => String(url || "").trim())
        .filter(Boolean)
    ),
  ];
  const links = (campaign.resourceLinks || []).map((url) => String(url).trim()).filter(Boolean);

  const heroActions = isOwner ? (
    <>
      <Button size="m" icon={<Icon name="wallet" size={20} />} onClick={() => setShowTopUp(true)}>
        {t("campaignFlow.topUpTitle")}
      </Button>
      <Button size="m" variant="gray" icon={<Icon name="edit" size={20} />} onClick={() => navigate(`/campaigns/${id}/edit`)}>
        {t("campaigns.editCampaign")}
      </Button>
      {open && (
        <Button size="m" variant="gray" icon={<Icon name="close" size={20} />} onClick={() => setShowStopModal(true)}>
          {t("campaigns.stopCampaign")}
        </Button>
      )}
      <IconButton size="m" label={t("common.share")} onClick={handleShare}>
        <Icon name="share" size={22} />
      </IconButton>
    </>
  ) : (
    <>
      <Button icon={<Icon name="upload" size={20} />} disabled={!open} onClick={() => setShowSubmissionModal(true)}>
        {t("campaigns.submitEntry")}
      </Button>
      <IconButton size="m" label={t("common.share")} onClick={handleShare}>
        <Icon name="share" size={22} />
      </IconButton>
    </>
  );

  const tabs = isOwner
    ? [
        { value: "overview", label: t("campaignFlow.overview") },
        { value: "review", label: `${t("campaignFlow.submissions")} · ${submissions.length}` },
        { value: "analytics", label: t("campaignFlow.analytics") },
      ]
    : [
        { value: "overview", label: t("campaignFlow.overview") },
        { value: "mine", label: t("campaignFlow.mySubmissions") },
        { value: "accounts", label: t("campaignFlow.accounts") },
      ];

  const brief = (
    <div className="flex flex-col gap-space-16">
      <p className="text-caps-label uppercase text-label-tertiary">{t("campaignFlow.aboutCampaign")}</p>
      {campaign.projectDescription && (
        <p className="whitespace-pre-wrap text-body text-label-primary">{campaign.projectDescription}</p>
      )}
      {campaign.contentGuidelines && (
        <div className="flex flex-col gap-space-8 rounded-[22px] bg-bg-inset p-space-16">
          <p className="text-subheadline-semibold text-label-primary">{t("campaigns.contentGuidelines")}</p>
          <p className="whitespace-pre-wrap text-subheadline text-label-secondary">{campaign.contentGuidelines}</p>
        </div>
      )}
      <div className="flex flex-col gap-space-8">
        <p className="text-caps-label uppercase text-label-tertiary">{t("campaigns.requirements")}</p>
        {requirements.length > 0 ? (
          <div className="flex flex-wrap gap-space-8">
            {requirements.map((requirement, index) => (
              <span
                key={index}
                className="inline-flex min-h-[28px] items-center rounded-full bg-fill-tertiary px-space-8 text-footnote-semibold text-label-primary"
              >
                {requirement}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-subheadline text-label-secondary">{t("campaigns.noSpecificRequirements")}</p>
        )}
      </div>
      {(attachments.length > 0 || links.length > 0) && (
        <div className="flex flex-col gap-space-8">
          <p className="text-caps-label uppercase text-label-tertiary">{t("campaigns.campaignResources")}</p>
          <div className="grid grid-cols-1 gap-space-8 min-[600px]:grid-cols-2">
            {attachments.map((href) => (
              <ResourceLink key={href} href={href} icon="file">
                {fileLabelFromUrl(href)}
              </ResourceLink>
            ))}
            {links.map((href) => (
              <ResourceLink key={href} href={href} icon="link">
                {href}
              </ResourceLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const stats = (
    <div className="grid grid-cols-1 gap-space-12 min-[600px]:grid-cols-2">
      <Stat label={t("campaigns.paidOut")}>
        <p className="text-headline text-label-primary">{budget > 0 ? `${money(spent)} / ${money(budget)}` : "—"}</p>
        {budget > 0 && (
          <>
            <ProgressBar value={used} />
            <div className="flex items-center justify-between gap-space-8">
              <p className="text-caption-1-semibold text-label-secondary">{Math.round(used * 100)}%</p>
              <p className="truncate text-caption-1 text-label-tertiary">
                {spent >= budget ? t("campaigns.budgetExhausted") : t("campaignFlow.remainingAmount", { amount: money(budget - spent) })}
              </p>
            </div>
          </>
        )}
      </Stat>
      <Stat label={t("campaignFlow.ratePerViews", { count: views })}>
        {rateRows.length === 0 && <p className="text-headline text-label-primary">—</p>}
        {rateRows.map((row) =>
          row.key ? (
            <div key={row.key} className="flex items-center gap-space-8">
              <PlatformLogo platform={platformForRateKey(row.key)} size={16} />
              <p className="min-w-0 flex-1 truncate text-footnote-semibold text-label-secondary">{getPlatformShortLabel(row.key)}</p>
              <p className="text-footnote-semibold text-accent-text">{row.rate} ₾</p>
            </div>
          ) : (
            <p key="rate" className="text-headline text-label-primary">
              {row.rate} ₾
            </p>
          )
        )}
      </Stat>
      <Stat label={t("campaigns.type")}>
        <p className="text-headline text-label-primary">{getParticipantTypeLabel(t, campaign.participantType)}</p>
      </Stat>
      <Stat label={t("campaigns.endDate")}>
        <p className="text-headline text-label-primary">{endDate}</p>
      </Stat>
    </div>
  );

  const createdBy = campaign.createdBy && (
    <div className="flex flex-wrap items-center gap-space-8">
      <p className="text-caps-label uppercase text-label-secondary">{t("campaigns.createdBy")}</p>
      <UserProfileLink
        user={campaign.createdBy}
        avatarSize="sm"
        isDark={isDark}
        nameClassName="text-footnote text-label-primary"
        fallback={campaign.createdBy.businessName || t("common.user")}
      />
      <span className="flex-1" />
      {!isOwner && messageStatus?.hasConversation && (
        <Button
          size="s"
          variant="gray"
          icon={<Icon name="message" size={16} />}
          onClick={() => navigate(`/messages/${messageStatus.conversationId}`)}
        >
          {t("campaigns.sendMessage")}
        </Button>
      )}
    </div>
  );

  const requestAction = !isOwner && messageStatus && !messageStatus.hasConversation && (
    messageStatus.hasPendingRequest && messageStatus.isSender ? (
      <Button size="m" variant="gray" fullWidth disabled>
        {t("campaigns.requestSent")}
      </Button>
    ) : messageStatus.canSendRequest && !messageStatus.hasPendingRequest ? (
      <Button
        size="m"
        variant="gray"
        fullWidth
        icon={<Icon name="message" size={20} />}
        onClick={() => setShowMessageRequestModal(true)}
      >
        {t("campaigns.sendMessageRequest")}
      </Button>
    ) : null
  );

  return (
    <AppScope app="creator-rewards" className="flex w-full flex-col gap-space-24 font-ds">
      <CampaignHero
        campaign={campaign}
        t={t}
        actions={heroActions}
        note={!isOwner && !open ? <p className="text-footnote text-label-secondary">{t("campaigns.submissionsClosed")}</p> : null}
      />

      {user && <ViewTabs label={campaign.campaignName} value={tab} onChange={setTab} options={tabs} selectedClass={SELECTED_TAB} />}

      {tab === "overview" && (
        <div className="flex flex-col gap-space-24 min-[1200px]:flex-row min-[1200px]:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-space-12 rounded-radius-lg bg-bg-raised p-space-16 min-[600px]:p-space-24">
            {isOwner ? stats : brief}
            {isOwner ? brief : stats}
            {createdBy}
          </div>
          <aside className="flex w-full flex-col gap-space-24 min-[1200px]:w-[320px] min-[1200px]:shrink-0">
            <CampaignPeopleCard
              campaign={campaign}
              setToast={setToast}
              onSeeAll={onNavigate ? () => onNavigate("members") : undefined}
              action={requestAction}
            >
              {!isOwner && (
                <InlineMessage title={t("campaignFlow.publishNowTitle")}>{t("campaigns.onlyViewsAfterSubmit")}</InlineMessage>
              )}
            </CampaignPeopleCard>
            <section className="flex flex-col gap-space-8 rounded-radius-lg bg-bg-raised p-space-16">
              <p className="text-caps-label uppercase text-label-secondary">{t("campaigns.paidOut")}</p>
              {budget > 0 && (
                <>
                  <p className="text-title-2 text-label-primary">{(used * 100).toFixed(1)}%</p>
                  <p className="text-footnote text-label-secondary">
                    {money(spent)} / {money(budget)}
                  </p>
                  <span className="my-space-4 h-px bg-separator-default" />
                  {spent >= budget ? (
                    <p className="text-subheadline-semibold text-status-warning">{t("campaigns.budgetExhausted")}</p>
                  ) : (
                    <p className="text-subheadline-semibold text-label-primary">
                      {t("campaignFlow.remainingAmount", { amount: money(budget - spent) })}
                    </p>
                  )}
                </>
              )}
              <p className="text-caps-label uppercase text-label-secondary">{t("campaigns.maxPayout")}</p>
              <p className="text-headline text-label-primary">
                {campaign.enableMaxPayout && campaign.maxPayout ? `${campaign.maxPayout} ₾` : t("campaigns.noMaximum")}
              </p>
            </section>
          </aside>
        </div>
      )}

      {tab === "mine" && (
        <MySubmissions
          submissions={ownSubmissions}
          setSubmissions={setSubmissions}
          refresh={loadSubmissions}
          setToast={setToast}
          isDark={isDark}
          onSubmit={open ? () => setShowSubmissionModal(true) : undefined}
        />
      )}

      {tab === "accounts" && (
        <ConnectedAccounts
          platforms={platforms}
          intro={t("campaignFlow.accountsIntro")}
          help={t("campaignFlow.accountsHelp", { platforms: platforms.map((platform) => platform.name).join(", ") })}
          setToast={setToast}
          isDark={isDark}
        />
      )}

      {tab === "review" && (
        <SubmissionsReview
          submissions={submissions}
          setSubmissions={setSubmissions}
          refresh={loadSubmissions}
          platforms={platforms}
          setToast={setToast}
          isDark={isDark}
        />
      )}

      {tab === "analytics" && <CampaignAnalytics campaign={campaign} submissions={submissions} />}

      <Alert
        open={showStopModal}
        title={t("campaigns.stopCampaignQuestion")}
        message={`${t("campaigns.confirmStopCampaign")} ${t("campaigns.stopCampaignWarning")}`}
        confirmLabel={t("campaigns.stopCampaign")}
        cancelLabel={t("common.cancel")}
        loading={isStopping}
        onConfirm={handleStop}
        onClose={() => setShowStopModal(false)}
      />

      {isOwner && (
        <TopUpBudgetModal
          open={showTopUp}
          onClose={() => setShowTopUp(false)}
          campaign={campaign}
          setToast={setToast}
          isDark={isDark}
          onFunded={(amount, response) =>
            setCampaign((prev) => (response?.data ? { ...prev, budget: response.data.budget } : { ...prev, budget: (prev.budget || 0) + amount }))
          }
        />
      )}

      <CampaignSubmissionModal
        isOpen={showSubmissionModal}
        setIsOpen={setShowSubmissionModal}
        campaign={campaign}
        isDark={isDark}
        setToast={setToast}
        onSubmitted={loadSubmissions}
        onViewSubmissions={() => setTab("mine")}
      />

      <Modal
        open={showMessageRequestModal}
        onClose={() => {
          setShowMessageRequestModal(false);
          setMessageRequestDraft("");
        }}
        title={t("campaigns.sendMessageRequest")}
        description={t("campaigns.messageRequestIntro")}
        closeLabel={t("common.close")}
        isDark={isDark}
      >
        <textarea
          value={messageRequestDraft}
          onChange={(event) => setMessageRequestDraft(event.target.value)}
          rows={5}
          placeholder={t("messages.writeMessage")}
          className="w-full resize-none rounded-[22px] bg-fill-tertiary px-space-16 py-space-12 text-callout text-label-primary outline-none placeholder:text-label-tertiary focus:shadow-[0_0_0_2px_var(--color-focus-ring)]"
        />
        <div className="flex justify-end gap-space-8">
          <Button
            variant="gray"
            size="m"
            disabled={isSendingRequest}
            onClick={() => {
              setShowMessageRequestModal(false);
              setMessageRequestDraft("");
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button size="m" className={isSendingRequest ? "" : APP_FILLED} loading={isSendingRequest} onClick={handleSendMessageRequest}>
            {isSendingRequest ? t("campaigns.sending") : t("campaigns.sendMessageRequest")}
          </Button>
        </div>
      </Modal>
    </AppScope>
  );
};

export default CampaignDetailsPage;
